import { TimerIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import type { KeyboardEvent } from "react";
import { useCallback, useState } from "react";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import SidneyCard from "../../../basic/SidneyCard";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import { ShareModes } from "../../../enums/shareFlow";
import { ActivityVisibility, resolveTestActivityVisibility } from "../../../helpers/ActivityVisibility";
import {
    getDateFromMilliseconds,
    getReadableTimeFromTimeLimit,
    getTimeFromMilliseconds,
    isDeadlineAlreadyPassed,
} from "../../../helpers/DateTime";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import { useTick } from "../../../hooks/useTick";
import { AreaButton, ButtonVariant } from "../../teacherArea/AreaButton";
import OnlineTestPopup from "../OnlineTestPopup";
import OnlineTestIntroInput from "./OnlineTestIntroInput";

interface OnlineTestIntroProps {
    studentName: string;
    onNameChange: (name: string) => void;
    onStart: () => void;

    /** Shown as the card title only when the teacher left no message, deadline or time limit. */
    testName?: string;
    teacherMessage?: string;
    dueDate?: number;
    timeLimit?: string;
    visibilityDate?: number | null;
    bookInfo?: string;
    isStartDisabled?: boolean;

    shareMode?: ShareModes | null;
    /** Legacy tests predate class-member identity — a typed name is the only one available, hence the name input. */
    isLegacy: boolean;
    classMemberName?: string;
    onSwitchAccount?: () => void;
    isOwner?: boolean;
    isPreview?: boolean;
}

const OnlineTestIntro = (props: OnlineTestIntroProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const isPractice = props.shareMode === ShareModes.PRACTICE;
    // A classMemberName is only set when the student is logged in to the test's class, so its
    // presence is what unlocks the greeting + "Account wechseln" link.
    const showGreeting = !!props.classMemberName;

    const { visibility, plannedDate } = !props.isLegacy
        ? resolveTestActivityVisibility(props.visibilityDate)
        : { visibility: ActivityVisibility.VISIBLE, plannedDate: null };
    const isHidden = visibility === ActivityVisibility.HIDDEN;
    const plannedMs = plannedDate ? plannedDate.valueOf() : null;
    // Ticker auto-stops when wall-clock passes plannedMs — re-render evaluates enabled=false, cleanup runs.
    const now = useTick(1000, plannedMs !== null && plannedMs > Date.now());
    const plannedFrom = plannedMs !== null && plannedMs > now ? plannedMs : null;
    const isBlocked = isHidden || plannedFrom !== null;

    const t_greeting = t("Hello {name}", { _tags: "OnlineTest,OnlineTestIntro", name: props.classMemberName ?? "" });
    const t_switchAccount = t("Not you? Switch account", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_dueDate = t("Due date:", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_timeLimit = t("Time limit:", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_yourName = t("Your name", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_startNow = t("Start Now", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_startPractice = t("Start practice now", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_preview = t("Preview", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_hour = t("h", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_deadlinePassedInfo = t(
        "Sadly the deadline for this Exercise has already passed, and you cannot take part in it anymore.",
        { _tags: "OnlineTest,OnlineTestIntro" }
    );
    const t_deadlinePassedAction = t("Please ask your teacher if he can extend the deadline.", {
        _tags: "OnlineTest,OnlineTestIntro",
    });
    const t_modalCancel = t("Cancel", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_modalContinue = t("Continue", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_modalTimeWarningBold = t("Keep an eye on the time!", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_modalNoteBold = t("Note:", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_modalNote = t(
        'By clicking "Continue", your name and answers will be saved. Both will be automatically deleted after 1 year - or when your teacher deletes the exercise.',
        { _tags: "OnlineTest,OnlineTestIntro" }
    );
    const t_practiceLabel = t("PRACTICE", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_practiceHint = t("These exercises are a practice. You can do them as often as you want.", {
        _tags: "OnlineTest,OnlineTestIntro",
    });
    const t_practiceNotSentToTeacher = t("Your answers will not be sent to your teacher.", {
        _tags: "OnlineTest,OnlineTestIntro",
    });
    const t_practicePopupNote = t(
        "You can repeat this practice as often as you want. Your answers will not be saved.",
        { _tags: "OnlineTest,OnlineTestIntro" }
    );
    const formattedTimeLimit = props.timeLimit ? getReadableTimeFromTimeLimit(props.timeLimit) : "";
    const t_modalTimeWarning = t(
        "After {timeLimit}, your answers will be sent to your teacher. You cannot pause the time.",
        { _tags: "OnlineTest,OnlineTestIntro", timeLimit: formattedTimeLimit }
    );
    const t_confirmationModalLabel = t("Confirmation", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_testHidden = isPractice
        ? t("Your teacher has hidden this practice.", { _tags: "OnlineTest,OnlineTestIntro" })
        : t("Your teacher has hidden this test.", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_plannedInfo = isPractice
        ? t("This practice is not yet available.", { _tags: "OnlineTest,OnlineTestIntro" })
        : t("This test is not yet available.", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_countdownLabel = t("Countdown:", { _tags: "OnlineTest,OnlineTestIntro" });
    const t_defaultGoodLuck = t("Good luck :)", { _tags: "OnlineTest,OnlineTestIntro" });

    const hasDeadlinePassed = props.dueDate ? isDeadlineAlreadyPassed(props.dueDate) : false;
    const isTeacherPreview = !!props.isOwner || !!props.isPreview;
    // Teacher-preview overrides: button always enabled (even for HIDDEN/PLANNED gates) and skips the
    // submission-warning popup — a teacher can't submit anyway.
    const isButtonDisabled = isTeacherPreview
        ? false
        : isBlocked || (props.isStartDisabled ?? (props.isLegacy && props.studentName.trim() === ""));
    const showTimingSection = !isPractice && (props.dueDate || props.timeLimit);

    const formatCountdown = (ms: number): string => {
        const total = Math.max(0, Math.floor(ms / 1000));
        const days = Math.floor(total / 86400);
        const hours = Math.floor((total % 86400) / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        const ss = seconds.toString().padStart(2, "0");
        if (days === 1) {
            return t("1 day", { _tags: "OnlineTest,OnlineTestIntro" });
        }
        if (days > 1) {
            return t("{days} days", { _tags: "OnlineTest,OnlineTestIntro", days });
        }
        if (hours > 0) {
            return t("{h}:{mm}:{ss} hrs", {
                _tags: "OnlineTest,OnlineTestIntro",
                h: hours,
                mm: minutes.toString().padStart(2, "0"),
                ss,
            });
        }
        return t("{m}:{ss} min", { _tags: "OnlineTest,OnlineTestIntro", m: minutes, ss });
    };

    const onStart = props.onStart;
    // A teacher can't submit, so the warning copy doesn't apply.
    const handleStartClick = useCallback(() => {
        if (isTeacherPreview) {
            onStart();
        } else {
            setIsPopupOpen(true);
        }
    }, [isTeacherPreview, onStart]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== "Enter" || isButtonDisabled) return;
            handleStartClick();
        },
        [isButtonDisabled, handleStartClick]
    );

    const handlePopupClose = useCallback(() => {
        setIsPopupOpen(false);
    }, []);

    const handlePopupConfirm = useCallback(() => {
        setIsPopupOpen(false);
        onStart();
    }, [onStart]);

    const renderPracticeInfoCard = () => {
        if (!isPractice) return null;

        return (
            <div
                className={`bg-white border border-p6-grey-lightest rounded-button overflow-hidden w-full flex flex-col gap-xs items-start justify-center ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "p-l",
                    "p-m"
                )}`}
                data-testid="online-test-practice-info"
            >
                <p className="text-xl text-p6-orange font-semibold">{t_practiceLabel}</p>
                <p className="text-l text-p6-grey-dark">{t_practiceHint}</p>
                <p className="text-l text-p6-grey-dark">{t_practiceNotSentToTeacher}</p>
            </div>
        );
    };

    const renderMessageSection = () => {
        if (!props.teacherMessage) return null;

        return (
            <div
                className={`flex items-center justify-center w-full ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "px-xxl",
                    "px-s"
                )}`}
            >
                <p className="text-l text-p6-grey-dark text-center whitespace-pre-wrap">{props.teacherMessage}</p>
            </div>
        );
    };

    const renderTimingSection = () => {
        if (!showTimingSection) return null;

        return (
            <div
                className={`flex justify-center w-full ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "flex-row items-start px-xxl",
                    "flex-col items-center px-s"
                )}`}
            >
                {/* !! guards against dueDate === 0, which React would otherwise render as a literal "0" */}
                {!!props.dueDate && (
                    <div
                        className={`flex-1 flex flex-col gap-xs items-center justify-center py-m w-full ${
                            props.timeLimit
                                ? `${getStyleBasedOnBreakpoint(
                                      Breakpoint.MD,
                                      "border-r",
                                      "border-b"
                                  )} border-p6-grey-light`
                                : ""
                        }`}
                    >
                        <div
                            className={`flex gap-xs items-center justify-center ${getStyleBasedOnBreakpoint(
                                Breakpoint.MD,
                                "flex-col",
                                "flex-row"
                            )}`}
                        >
                            <PhaseSixIcon
                                name="calendar-cross"
                                className="text-[30px] text-p6-grey-dark"
                            />
                            <p className="text-l text-p6-grey-dark">{t_dueDate}</p>
                        </div>

                        <div className="flex flex-row items-center gap-xs">
                            <p className="text-l text-p6-grey-dark">{getDateFromMilliseconds(props.dueDate)}</p>
                            <p className="text-l text-p6-grey-dark">-</p>
                            <div className="flex flex-row items-center gap-xs">
                                <p className="text-l text-p6-grey-dark">{getTimeFromMilliseconds(props.dueDate)}</p>
                                <p className="text-l text-p6-grey-dark">{t_hour}</p>
                            </div>
                        </div>
                    </div>
                )}

                {props.timeLimit && (
                    <div className="flex-1 flex flex-col gap-xs items-center justify-center py-m w-full">
                        <div
                            className={`flex gap-xs items-center justify-center ${getStyleBasedOnBreakpoint(
                                Breakpoint.MD,
                                "flex-col",
                                "flex-row"
                            )}`}
                        >
                            <PhosphorIcon
                                phosphorIcon={TimerIcon}
                                color="text-p6-grey-dark"
                                fontSize="text-[30px]"
                            />
                            <p className="text-l text-p6-grey-dark">{t_timeLimit}</p>
                        </div>
                        <p className="text-l text-p6-grey-dark">{formattedTimeLimit}</p>
                    </div>
                )}
            </div>
        );
    };

    // A test without message, deadline and time limit would otherwise show no card at all — fall back
    // to the test title plus a friendly default line so the intro doesn't look broken.
    const renderFallbackSection = () => {
        return (
            <div
                className={`flex flex-col gap-xs items-center justify-center w-full ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "px-xxl",
                    "px-s"
                )}`}
                data-testid="online-test-intro-fallback"
            >
                {props.testName && (
                    <p className="text-xl text-p6-grey-dark font-semibold text-center">{props.testName}</p>
                )}
                <p className="text-l text-p6-grey-dark text-center">{t_defaultGoodLuck}</p>
            </div>
        );
    };

    const renderExerciseIntroCard = () => {
        // Practice mode already shows its own info card above, so no fallback is needed there.
        if (isPractice && !props.teacherMessage) return null;
        const hasContent = props.teacherMessage || showTimingSection;

        return (
            <div
                className={`bg-white border border-p6-grey-lightest rounded-button overflow-hidden w-full flex flex-col gap-m items-center justify-center ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "py-xl",
                    "py-m"
                )}`}
                data-testid="online-test-intro-card"
            >
                {hasContent ? (
                    <>
                        {renderMessageSection()}
                        {renderTimingSection()}
                    </>
                ) : (
                    renderFallbackSection()
                )}
            </div>
        );
    };

    const getStartButtonLabel = () => {
        if (isTeacherPreview) return t_preview;
        if (isPractice) return t_startPractice;
        return t_startNow;
    };

    const renderStartButton = () => {
        return (
            <div
                className="w-full grid"
                data-testid="online-test-start-button"
            >
                <AreaButton
                    variant={ButtonVariant.PRIMARY}
                    role="button"
                    onClick={handleStartClick}
                    disabled={isButtonDisabled}
                >
                    {getStartButtonLabel()}
                </AreaButton>
            </div>
        );
    };

    const renderDeadlinePassedCard = () => {
        return (
            <div
                className={`bg-white border border-p6-grey-lightest rounded-button overflow-hidden w-full flex items-center justify-center ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "flex-row gap-l p-xl",
                    "flex-col gap-s p-m"
                )}`}
            >
                <PhaseSixIcon
                    name="alert"
                    className="text-[56px] text-p6-grey-dark flex-shrink-0"
                />
                <div className="flex flex-col gap-s text-center">
                    <p className="text-l text-p6-grey-dark">{t_deadlinePassedInfo}</p>
                    <p className="text-l text-p6-grey-dark">{t_deadlinePassedAction}</p>
                </div>
            </div>
        );
    };

    const renderInputSection = () => {
        if (!props.isLegacy) return null;

        return (
            <OnlineTestIntroInput
                data-testid="online-test-name-input"
                value={props.studentName}
                onChange={props.onNameChange}
                onKeyDown={handleKeyDown}
                placeholder={t_yourName}
            />
        );
    };

    const renderConfirmationModalContent = () => {
        if (isPractice) {
            return <p className="text-l text-p6-grey-dark">{t_practicePopupNote}</p>;
        }

        return (
            <>
                {props.timeLimit && (
                    <div
                        className={`flex gap-s items-center ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "flex-row",
                            "flex-col text-center"
                        )}`}
                    >
                        <div
                            className={`flex flex-row items-center justify-center ${getStyleBasedOnBreakpoint(
                                Breakpoint.MD,
                                "min-w-[var(--size-icon-l)] min-h-[var(--size-icon-l)]",
                                "min-w-xl min-h-xl"
                            )}`}
                        >
                            {/* This icon uses a different text-[x] as the one below because the PhaseSixIcon have some default padding */}
                            <PhosphorIcon
                                phosphorIcon={TimerIcon}
                                color="text-p6-grey-dark"
                                fontSize={`flex-shrink-0 ${getStyleBasedOnBreakpoint(
                                    Breakpoint.MD,
                                    "text-[46px]",
                                    "text-[34px]"
                                )}`}
                            />
                        </div>

                        <p className="text-m text-p6-grey-dark">
                            <span className="font-semibold">{t_modalTimeWarningBold}</span> {t_modalTimeWarning}
                        </p>
                    </div>
                )}

                <div
                    className={`flex gap-s items-center ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "flex-row",
                        "flex-col text-center"
                    )}`}
                >
                    <PhaseSixIcon
                        name="info"
                        className={`text-p6-grey-dark flex-shrink-0 ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "text-[56px]",
                            "text-[40px]"
                        )}`}
                    />
                    <p className="text-m text-p6-grey-dark">
                        <span className="font-semibold">{t_modalNoteBold}</span> {t_modalNote}
                    </p>
                </div>
            </>
        );
    };

    const renderHiddenCard = () => {
        return (
            <SidneyCard dataTestId="online-test-hidden-card">
                <p className="text-l text-p6-grey-dark">{t_testHidden}</p>
            </SidneyCard>
        );
    };

    const renderPlannedCard = (visibilityDateMs: number) => {
        const remainingMs = Math.max(0, visibilityDateMs - now);
        const t_plannedStart = t("You can start it on {date} at {time}.", {
            _tags: "OnlineTest,OnlineTestIntro",
            date: getDateFromMilliseconds(visibilityDateMs),
            time: getTimeFromMilliseconds(visibilityDateMs),
        });
        return (
            <SidneyCard dataTestId="online-test-planned-card">
                <div className="flex flex-col gap-l w-full">
                    <div className="flex flex-col gap-xs">
                        <p className="text-l text-p6-grey-dark">{t_plannedInfo}</p>
                        <p className="text-l text-p6-grey-dark">{t_plannedStart}</p>
                    </div>
                    <div className="flex flex-col w-full">
                        <p className="text-xl text-p6-grey-dark w-full">{t_countdownLabel}</p>
                        <p
                            className="text-xxl text-p6-grey-dark font-semibold tabular-nums w-full"
                            aria-live="off"
                            data-testid="online-test-countdown"
                        >
                            {formatCountdown(remainingMs)}
                        </p>
                    </div>
                </div>
            </SidneyCard>
        );
    };

    const renderConfirmationModal = () => {
        return (
            <OnlineTestPopup
                data-testid="online-test-confirm-dialog"
                isOpen={isPopupOpen}
                onClose={handlePopupClose}
                ariaLabel={t_confirmationModalLabel}
                primaryAction={{
                    label: t_modalContinue,
                    accessibilityLabel: t_modalContinue,
                    onClick: handlePopupConfirm,
                    "data-testid": "online-test-confirm-button",
                }}
                secondaryAction={{
                    label: t_modalCancel,
                    accessibilityLabel: t_modalCancel,
                    onClick: handlePopupClose,
                    "data-testid": "online-test-cancel-button",
                }}
            >
                {renderConfirmationModalContent()}
            </OnlineTestPopup>
        );
    };

    const renderGreetingRow = () => {
        if (!showGreeting) return null;

        return (
            <div
                className={`flex w-full ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "flex-row items-end justify-between",
                    "flex-col items-start gap-xs"
                )}`}
            >
                <p
                    className={`${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "text-xxl",
                        "text-xl"
                    )} text-p6-grey-dark font-semibold`}
                    data-testid="online-test-greeting"
                >
                    {t_greeting}
                </p>
                {props.onSwitchAccount && (
                    <button
                        type="button"
                        onClick={props.onSwitchAccount}
                        className={`flex items-center gap-xxs bg-transparent border-none cursor-pointer p-no ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "self-auto",
                            "self-end"
                        )}`}
                        data-testid="online-test-switch-account"
                    >
                        <span className="text-l text-p6-grey-dark">{t_switchAccount}</span>
                        <PhaseSixIcon
                            name="chevron-right"
                            className="text-[24px] text-p6-grey-dark"
                        />
                    </button>
                )}
            </div>
        );
    };

    return (
        <>
            <div className={`flex flex-col w-full grow ${getStyleBasedOnBreakpoint(Breakpoint.MD, "px-no", "px-s")}`}>
                <div
                    className={`flex-1 flex flex-col items-center gap-s w-full py-l ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "justify-center",
                        "justify-start"
                    )}`}
                >
                    {renderGreetingRow()}
                    {/* Passed deadline, hidden and planned each replace the whole intro — the teacher
                        preview included, so it shows the same dead end a student gets. The preview's
                        way past it is the button below, which stays enabled. */}
                    {hasDeadlinePassed ? (
                        renderDeadlinePassedCard()
                    ) : (
                        <>
                            {isHidden && renderHiddenCard()}
                            {plannedFrom !== null && renderPlannedCard(plannedFrom)}
                            {!isBlocked && (
                                <>
                                    {renderPracticeInfoCard()}
                                    {renderExerciseIntroCard()}
                                    {renderInputSection()}
                                </>
                            )}
                        </>
                    )}
                    {(!hasDeadlinePassed || isTeacherPreview) && renderStartButton()}
                    {props.bookInfo && (
                        <p className="text-p6-grey-mid text-m text-center w-full px-s">{props.bookInfo}</p>
                    )}
                </div>
            </div>
            {renderConfirmationModal()}
        </>
    );
};

export default OnlineTestIntro;
